import {
  DateRangeField,
  FlexColumn,
  FlexMiddle,
  FormModal,
  SelectField,
  SmallBody
} from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT, CONTRACT_TYPES_WITH_TOIL_ACCRUAL, PTO_TYPES } from '@ubeya/shared/constants';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import usePtoTypes from '@ubeya/shared/hooks/generic/usePtoTypes';
import useToaster from '@ubeya/shared/hooks/useToaster';
import { required } from '@ubeya/shared/utils/form';
import { getTotalDays } from '@ubeya/shared/utils/time';
import moment from 'moment';
import React, { useCallback, useContext, useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';

const Container = styled(FlexColumn)`
  gap: 16px;
`;

const FieldsContainer = styled(FlexMiddle)`
  gap: 16px;
  > div {
    width: 100%;
  }
`;

const SelectedDaysCounter = styled(SmallBody)`
  min-height: 46px;
  margin-top: calc(3px + 20rem);
  display: flex;
  align-items: center;
`;

const AddUsedPtoModal = ({ onClose, addPtoDays, employeeBranches = [], eligibleStartDate, activeContract }) => {
  const { t } = useContext(ConfigContext);
  const { addResponseError } = useToaster();
  const { branchesOptions } = useBranches();
  const { ptoTypesOptions } = usePtoTypes();

  const supportsToil = useMemo(() => CONTRACT_TYPES_WITH_TOIL_ACCRUAL.includes(activeContract?.accountContract?.type), [
    activeContract?.accountContract?.type
  ]);

  const addUsedPtoTypeOptions = useMemo(
    () => (supportsToil ? ptoTypesOptions : ptoTypesOptions.filter(({ value }) => value !== PTO_TYPES.TimeOffInLieu)),
    [ptoTypesOptions, supportsToil]
  );

  const branchPickerOptions = useMemo(() => {
    const employeeBranchesSet = new Set(employeeBranches);
    return branchesOptions.filter(({ value }) => employeeBranchesSet.has(value));
  }, [branchesOptions, employeeBranches]);

  const isDayBlocked = useCallback(
    (d) => {
      return eligibleStartDate && moment(d).isBefore(moment(eligibleStartDate, API_DATE_FORMAT), 'day');
    },
    [eligibleStartDate]
  );

  const onSubmit = useCallback(
    async (values) => {
      const { dates, branchId, type } = values;
      const { startDate, endDate } = dates || {};
      if (!branchId || !startDate || !endDate || !type) return;

      try {
        await addPtoDays({
          branchId,
          startDate: startDate.format(API_DATE_FORMAT),
          endDate: endDate.format(API_DATE_FORMAT),
          type
        });
        trackEvent('Leave management - added used PTO', { module: 'Leave Management', ptoType: type });
      } catch (error) {
        addResponseError(error);
      } finally {
        onClose();
      }
    },
    [addPtoDays, addResponseError, onClose]
  );

  return (
    <FormModal
      onSubmit={onSubmit}
      size="small"
      requiredWarningFooter={t('missingRequiredFields')}
      onClose={onClose}
      title={t('addUsedHolidays')}
      confirmText={t('add')}
      showCancelButton
      keepDirtyOnReinitialize
      disabledOnPristine={false}>
      <Container>
        <p>{t('addUsedHolidayDisclaimer')}</p>
        <p>{t('addUsedHolidayDisclaimer2')}</p>

        <FieldsContainer>
          <DateRangeField
            name="dates"
            title={t('holidayDates')}
            isDayBlocked={isDayBlocked}
            validate={required}
            required
            usePortal
          />
          <SelectedDaysDisplay />
        </FieldsContainer>

        <FieldsContainer>
          <SelectField
            name="type"
            title={t('type')}
            options={addUsedPtoTypeOptions}
            validate={required}
            required
            usePortal
            isMulti={false}
          />

          <SelectField
            name="branchId"
            title={t('branch')}
            options={branchPickerOptions}
            validate={required}
            required
            usePortal
            isMulti={false}
          />
        </FieldsContainer>
      </Container>
    </FormModal>
  );
};

const SelectedDaysDisplay = () => {
  const { t } = useContext(ConfigContext);
  const {
    input: { value: dates }
  } = useField('dates');

  const { startDate, endDate } = dates || {};

  const amountOfDays = startDate ? getTotalDays(startDate, endDate) : 0;

  return <SelectedDaysCounter>{`${amountOfDays} ${t('days')}`}</SelectedDaysCounter>;
};

export default AddUsedPtoModal;
